import { useState, useEffect } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import {
  useCreateInvoice,
  useUpdateInvoice,
  usePreviewInvoiceNumber,
} from "../hooks/use-invoices";
import { useCustomers } from "@/features/customers/hooks/use-customers";
import { useShippingRates } from "@/features/shipping-rates/hooks/use-shipping-rates";
import {
  calculateDueDate,
  type Invoice,
  type InvoiceItem,
} from "../types/invoice.types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Plus, Trash2, GripVertical, Copy } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  PRODUCT_TYPE_OPTIONS,
  UNIT_OPTIONS,
  PAYMENT_TERMS_OPTIONS,
  INVOICE_TYPE_OPTIONS,
  calculateItemAmount,
  type ProductType,
  type InvoiceType,
} from "../types/invoice.types";
import { useCompanyDetails } from "@/features/company-details/hooks/use-company-details";
import { useCurrencyFormat } from "@/hooks/use-currency-format";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "sonner";

// Schema creation function that accepts translation function
const createInvoiceItemSchema = (t: (key: string) => string) =>
  z.object({
    description: z
      .string()
      .min(1, t("invoices.form.validation.descriptionRequired")),
    productType: z.enum(["goods", "service"]),
    itemDate: z.string().min(1, t("invoices.form.validation.dateRequired")),
    quantity: z.number().min(0.01, t("invoices.form.validation.quantityMin")),
    unit: z.string().min(1, t("invoices.form.validation.unitRequired")),
    unitPrice: z.number().min(0, t("invoices.form.validation.priceMin")),
  });

const createInvoiceFormSchema = (t: (key: string) => string) =>
  z.object({
    customerId: z
      .string()
      .min(1, t("invoices.form.validation.customerRequired")),
    invoiceType: z.enum(["standard", "free"]),
    invoiceNumber: z.string().optional(), // Optional - will be auto-generated for standard
    invoiceDate: z
      .string()
      .min(1, t("invoices.form.validation.invoiceDateRequired")),
    paymentTerms: z.number(),
    purchaseOrderNumber: z.string().optional(),
    leitwegId: z.string().optional(),
    message: z.string().optional(),
    vatRate: z.string(),
    priceType: z.enum(["net", "gross"]),
    isSmallBusiness: z.boolean(),
    smallBusinessNote: z.string().optional(),
    shippingRateId: z.string().optional(),
    items: z
      .array(createInvoiceItemSchema(t))
      .min(1, t("invoices.form.validation.itemsMin")),
  });

type InvoiceFormValues = z.infer<ReturnType<typeof createInvoiceFormSchema>>;

// Sortable Item Component
interface SortableInvoiceItemProps {
  id: string;
  index: number;
  form: ReturnType<typeof useForm<InvoiceFormValues>>;
  calculateItemTotal: (quantity: number, unitPrice: number) => number;
  onRemove: () => void;
  onDuplicate: () => void;
  canRemove: boolean;
  formatCurrency: (amount: number | string | null | undefined) => string;
}

function SortableInvoiceItem({
  id,
  index,
  form,
  calculateItemTotal,
  onRemove,
  onDuplicate,
  canRemove,
  formatCurrency,
}: SortableInvoiceItemProps) {
  const { t } = useTranslation();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const quantity = form.watch(`items.${index}.quantity`) || 0;
  const unitPrice = form.watch(`items.${index}.unitPrice`) || 0;
  const total = calculateItemTotal(quantity, unitPrice);

  return (
    <>
      {/* Desktop View - Grid Layout */}
      <div
        ref={setNodeRef}
        style={{
          ...style,
          gridTemplateColumns:
            "50px 2fr 140px 160px 80px 80px 110px 140px 120px",
        }}
        className="bg-background hidden items-start gap-4 py-2 lg:grid"
        {...attributes}
      >
        {/* Drag Handle + # */}
        <div className="flex h-10 items-center gap-2">
          <button
            type="button"
            className="cursor-grab touch-none active:cursor-grabbing"
            {...listeners}
          >
            <GripVertical className="text-muted-foreground h-4 w-4" />
          </button>
          <span className="text-sm font-medium">{index + 1}</span>
        </div>

        {/* Beschreibung */}
        <FormField
          control={form.control}
          name={`items.${index}.description`}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Textarea
                  placeholder="Beschreibung der Leistung/Ware..."
                  className="min-h-10 resize-y"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Art */}
        <FormField
          control={form.control}
          name={`items.${index}.productType`}
          render={({ field }) => (
            <FormItem>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Wählen" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {PRODUCT_TYPE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Datum */}
        <FormField
          control={form.control}
          name={`items.${index}.itemDate`}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input type="date" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Einheit */}
        <FormField
          control={form.control}
          name={`items.${index}.unit`}
          render={({ field }) => (
            <FormItem>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Wählen" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {UNIT_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Menge */}
        <FormField
          control={form.control}
          name={`items.${index}.quantity`}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  type="text"
                  inputMode="decimal"
                  placeholder="1,00"
                  defaultValue={
                    field.value ? field.value.toString().replace(".", ",") : "1"
                  }
                  onBlur={(e) => {
                    const value = e.target.value.replace(",", ".");
                    const numValue = parseFloat(value);
                    field.onChange(
                      isNaN(numValue) || numValue <= 0 ? 1 : numValue,
                    );
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Einzelpreis */}
        <FormField
          control={form.control}
          name={`items.${index}.unitPrice`}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  type="text"
                  inputMode="decimal"
                  placeholder="0,00"
                  defaultValue={
                    field.value ? field.value.toString().replace(".", ",") : "0"
                  }
                  onBlur={(e) => {
                    const value = e.target.value.replace(",", ".");
                    const numValue = parseFloat(value);
                    field.onChange(
                      isNaN(numValue) || numValue < 0 ? 0 : numValue,
                    );
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Gesamtpreis */}
        <div className="border-input bg-muted flex h-10 items-center rounded-md border px-3 py-2 text-sm">
          {formatCurrency(
            calculateItemTotal(
              form.watch(`items.${index}.quantity`),
              form.watch(`items.${index}.unitPrice`),
            ),
          )}
        </div>

        {/* Duplicate and Remove Buttons */}
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-10"
            onClick={onDuplicate}
            title="Position kopieren"
          >
            <Copy className="h-4 w-4" />
          </Button>
          {canRemove && (
            <Button
              type="button"
              variant="destructive"
              size="sm"
              className="h-10"
              onClick={onRemove}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Mobile View - Card Layout */}
      <Card
        ref={setNodeRef}
        style={style}
        className="lg:hidden"
        {...attributes}
      >
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="cursor-grab touch-none active:cursor-grabbing"
                {...listeners}
              >
                <GripVertical className="text-muted-foreground h-4 w-4" />
              </button>
              <span className="text-sm font-medium">Position {index + 1}</span>
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onDuplicate}
                title="Kopieren"
              >
                <Copy className="h-3 w-3" />
              </Button>
              {canRemove && (
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={onRemove}
                  title="Löschen"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 pt-0">
          <FormField
            control={form.control}
            name={`items.${index}.description`}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs">
                  {t("invoices.form.itemDescription")}
                </FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Beschreibung..."
                    className="min-h-10 resize-y text-sm"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-2 gap-3">
            <FormField
              control={form.control}
              name={`items.${index}.productType`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs">
                    {t("invoices.form.itemProductType")}
                  </FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="h-9 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {PRODUCT_TYPE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={`items.${index}.itemDate`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs">
                    {t("invoices.form.itemDate")}
                  </FormLabel>
                  <FormControl>
                    <Input type="date" className="h-9 text-sm" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <FormField
              control={form.control}
              name={`items.${index}.unit`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs">
                    {t("invoices.form.itemUnit")}
                  </FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="h-9 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {UNIT_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={`items.${index}.quantity`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs">
                    {t("invoices.form.itemQuantity")}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      inputMode="decimal"
                      placeholder="1,00"
                      className="h-9 text-sm"
                      defaultValue={
                        field.value
                          ? field.value.toString().replace(".", ",")
                          : "1"
                      }
                      onBlur={(e) => {
                        const value = e.target.value.replace(",", ".");
                        const numValue = parseFloat(value);
                        field.onChange(
                          isNaN(numValue) || numValue <= 0 ? 1 : numValue,
                        );
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name={`items.${index}.unitPrice`}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs">
                  {t("invoices.form.itemUnitPrice")} (€)
                </FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    inputMode="decimal"
                    placeholder="0,00"
                    className="h-9 text-sm"
                    defaultValue={
                      field.value
                        ? field.value.toString().replace(".", ",")
                        : "0"
                    }
                    onBlur={(e) => {
                      const value = e.target.value.replace(",", ".");
                      const numValue = parseFloat(value);
                      field.onChange(isNaN(numValue) ? 0 : numValue);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex items-center justify-between border-t pt-2">
            <span className="text-sm font-medium">
              {t("invoices.form.itemTotal")}:
            </span>
            <span className="text-lg font-bold">{formatCurrency(total)}</span>
          </div>
        </CardContent>
      </Card>
    </>
  );
}

interface InvoiceFormProps {
  initialData?: Partial<Invoice> & { items?: InvoiceItem[] };
  onSuccess?: () => void;
  isReadOnly?: boolean;
  sidebarContent?: React.ReactNode;
  preselectedCustomerId?: string;
}

export function InvoiceForm({
  initialData,
  onSuccess,
  isReadOnly = false,
  sidebarContent,
  preselectedCustomerId,
}: InvoiceFormProps) {
  const { t } = useTranslation();
  const { formatCurrency } = useCurrencyFormat();
  const createInvoice = useCreateInvoice();
  const updateInvoice = useUpdateInvoice(initialData?.id || "");
  const { data: customers } = useCustomers();
  const { data: companyDetails } = useCompanyDetails();
  const { data: shippingRates } = useShippingRates();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditMode = !!initialData?.id;

  const form = useForm<InvoiceFormValues>({
    resolver: zodResolver(createInvoiceFormSchema(t)),
    defaultValues: initialData
      ? {
          customerId:
            (initialData as Invoice & { customerId?: string }).customerId || "",
          invoiceType:
            (initialData as Invoice & { invoiceType?: InvoiceType })
              .invoiceType || "standard",
          invoiceNumber: initialData.invoiceNumber || "",
          invoiceDate:
            (typeof initialData.invoiceDate === "string"
              ? initialData.invoiceDate.split("T")[0]
              : (initialData.invoiceDate as Date)
                  ?.toISOString()
                  .split("T")[0]) || new Date().toISOString().split("T")[0],
          paymentTerms: initialData.paymentTerms || 14,
          purchaseOrderNumber: initialData.purchaseOrderNumber || "",
          leitwegId: initialData.leitwegId || "",
          message: initialData.message || "",
          vatRate:
            initialData.vatRate ||
            companyDetails?.defaultVatRate.toString() ||
            "19",
          priceType: initialData.priceType || "net",
          isSmallBusiness: initialData.isSmallBusiness === "true",
          smallBusinessNote:
            initialData.smallBusinessNote ||
            companyDetails?.smallBusinessText ||
            "",
          shippingRateId:
            (initialData as Invoice & { shippingRateId?: string })
              .shippingRateId || "",
          items:
            initialData.items && initialData.items.length > 0
              ? initialData.items.map((item) => ({
                  description: item.description || "",
                  productType: item.productType || ("goods" as ProductType),
                  itemDate:
                    (typeof item.itemDate === "string"
                      ? item.itemDate.split("T")[0]
                      : (item.itemDate as Date)?.toISOString().split("T")[0]) ||
                    new Date().toISOString().split("T")[0],
                  quantity:
                    typeof item.quantity === "string"
                      ? parseFloat(item.quantity) || 1
                      : item.quantity || 1,
                  unit: item.unit || "Stk.",
                  unitPrice:
                    typeof item.unitPrice === "string"
                      ? parseFloat(item.unitPrice) || 0
                      : item.unitPrice || 0,
                }))
              : [
                  {
                    description: "",
                    productType: "goods" as ProductType,
                    itemDate: new Date().toISOString().split("T")[0],
                    quantity: 1,
                    unit: "Stk.",
                    unitPrice: 0,
                  },
                ],
        }
      : {
          customerId: preselectedCustomerId || "",
          invoiceType: "standard" as InvoiceType,
          invoiceNumber: "",
          invoiceDate: new Date().toISOString().split("T")[0],
          paymentTerms: 14,
          purchaseOrderNumber: "",
          leitwegId: "",
          message: "",
          vatRate: companyDetails?.defaultVatRate.toString() || "19",
          priceType: "net",
          isSmallBusiness: false,
          smallBusinessNote: companyDetails?.smallBusinessText || "",
          shippingRateId: "",
          items: [
            {
              description: "",
              productType: "goods" as ProductType,
              itemDate: new Date().toISOString().split("T")[0],
              quantity: 1,
              unit: "Stk.",
              unitPrice: 0,
            },
          ],
        },
  });

  const { fields, append, remove, move, insert } = useFieldArray({
    control: form.control,
    name: "items",
  });

  // Preview invoice number for new invoices (only for standard type)
  const watchCustomerId = form.watch("customerId");
  const watchInvoiceType = form.watch("invoiceType");
  const isStandard = watchInvoiceType === "standard";
  const { data: numberPreview, isLoading: isLoadingNumber } =
    usePreviewInvoiceNumber(watchCustomerId, !isEditMode && isStandard);

  // Set up drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = fields.findIndex((field) => field.id === active.id);
      const newIndex = fields.findIndex((field) => field.id === over.id);
      move(oldIndex, newIndex);
    }
  };

  // Set default VAT rate from company details when available (for new invoices)
  useEffect(() => {
    if (!initialData && companyDetails?.defaultVatRate !== undefined) {
      // Keep decimal places (e.g., "19.05" stays "19.05")
      form.setValue("vatRate", companyDetails.defaultVatRate.toString());
    }
  }, [companyDetails, initialData, form]);

  // Sync preview invoice number to form when available (only for standard invoices)
  useEffect(() => {
    if (!isEditMode && numberPreview?.number && isStandard) {
      form.setValue("invoiceNumber", numberPreview.number);
    }
    // Clear the number field when switching to free type in create mode
    if (!isEditMode && !isStandard) {
      form.setValue("invoiceNumber", "");
    }
  }, [numberPreview, isEditMode, isStandard, form]);

  const calculateItemTotal = (quantity: number, unitPrice: number) => {
    return calculateItemAmount(quantity, unitPrice);
  };

  const calculateSubtotal = () => {
    const items = form.watch("items");
    const itemsTotal = items.reduce(
      (sum: number, item: InvoiceFormValues["items"][0]) => {
        return (
          sum + calculateItemTotal(item.quantity || 0, item.unitPrice || 0)
        );
      },
      0,
    );

    const priceType = form.watch("priceType");
    const isSmallBusiness = form.watch("isSmallBusiness");

    // Add shipping cost to items total
    const shippingRateId = form.watch("shippingRateId");
    const shippingCost =
      shippingRateId && shippingRates
        ? parseFloat(
            shippingRates.find((r) => r.id === shippingRateId)?.price || "0",
          )
        : 0;
    const totalWithShipping = itemsTotal + shippingCost;

    // Wenn Brutto-Preise und Kleinunternehmer: totalWithShipping ist bereits Brutto
    if (priceType === "gross" && !isSmallBusiness) {
      // Bei Brutto-Preisen: Netto = Brutto / (1 + MwSt-Satz)
      const vatRate = parseFloat(form.watch("vatRate") || "19");
      return totalWithShipping / (1 + vatRate / 100);
    }

    // Bei Netto-Preisen oder Kleinunternehmer: totalWithShipping ist bereits Netto
    return totalWithShipping;
  };

  const calculateTax = (subtotal: number) => {
    const isSmallBusiness = form.watch("isSmallBusiness");
    if (isSmallBusiness) return 0;

    const vatRate = parseFloat(form.watch("vatRate") || "19");
    return subtotal * (vatRate / 100);
  };

  const calculateTotal = () => {
    const items = form.watch("items");
    const itemsTotal = items.reduce(
      (sum: number, item: InvoiceFormValues["items"][0]) => {
        return (
          sum + calculateItemTotal(item.quantity || 0, item.unitPrice || 0)
        );
      },
      0,
    );

    const priceType = form.watch("priceType");
    const isSmallBusiness = form.watch("isSmallBusiness");

    // Add shipping cost to items total
    const shippingRateId = form.watch("shippingRateId");
    const shippingCost =
      shippingRateId && shippingRates
        ? parseFloat(
            shippingRates.find((r) => r.id === shippingRateId)?.price || "0",
          )
        : 0;
    const totalWithShipping = itemsTotal + shippingCost;

    // Bei Brutto-Preisen: totalWithShipping ist bereits die Gesamtsumme
    if (priceType === "gross") {
      return totalWithShipping;
    }

    // Bei Netto-Preisen: Gesamtsumme = Netto + MwSt
    const subtotal = calculateSubtotal();
    const tax = calculateTax(subtotal);
    return subtotal + tax;
  };

  const onSubmit = async (data: InvoiceFormValues) => {
    setIsSubmitting(true);
    try {
      const dueDate = calculateDueDate(
        new Date(data.invoiceDate),
        data.paymentTerms,
      );
      const baseInvoiceData = {
        customerId: data.customerId,
        invoiceType: data.invoiceType,
        invoiceNumber:
          data.invoiceType === "free" ? data.invoiceNumber : undefined,
        invoiceDate: data.invoiceDate,
        paymentTerms: data.paymentTerms,
        dueDate: dueDate,
        purchaseOrderNumber: data.purchaseOrderNumber || undefined,
        leitwegId: data.leitwegId || undefined,
        message: data.message || undefined,
        vatRate: data.vatRate,
        priceType: data.priceType,
        isSmallBusiness: data.isSmallBusiness ? "true" : "false",
        smallBusinessNote: data.isSmallBusiness
          ? data.smallBusinessNote
          : undefined,
        shippingRateId: data.shippingRateId || undefined,
        items: data.items.map((item, index) => ({
          description: item.description,
          productType: item.productType,
          itemDate: item.itemDate,
          quantity: item.quantity,
          unit: item.unit,
          unitPrice: item.unitPrice,
          position: index + 1,
        })),
      };

      if (isEditMode) {
        const invoiceData = {
          ...baseInvoiceData,
          invoiceNumber: data.invoiceNumber!,
        };
        await updateInvoice.mutateAsync(invoiceData);
      } else {
        await createInvoice.mutateAsync(baseInvoiceData);
      }

      onSuccess?.();
    } catch (error) {
      let message = "Unbekannter Fehler";
      if (typeof error === "string") message = error;
      else if (error instanceof Error) message = error.message;
      else if (error && typeof error === "object" && "message" in error)
        message = (error as { message: string }).message;
      toast.error(message);
      console.error(
        `${t(isEditMode ? "invoices.form.errorUpdating" : "invoices.form.errorCreating")}:`,
        error,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-full space-y-6 overflow-hidden">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className={`w-full max-w-full space-y-6 ${isReadOnly ? "pointer-events-none opacity-75" : ""}`}
        >
          {isReadOnly && (
            <div className="pointer-events-auto mb-4 rounded-md border border-yellow-200 bg-yellow-50 p-4 opacity-100">
              <p className="text-sm text-yellow-800">
                {t("invoices.form.readOnlyWarning")}
              </p>
            </div>
          )}

          {/* Sidebar first on mobile, side-by-side on desktop */}
          <div
            className={
              sidebarContent
                ? "w-full max-w-full space-y-6 lg:grid lg:grid-cols-[1fr_300px] lg:gap-6 lg:space-y-0"
                : ""
            }
          >
            {/* Sidebar - shown first on mobile with order */}
            {sidebarContent && (
              <div className="pointer-events-auto w-full max-w-full opacity-100 lg:order-2">
                {sidebarContent}
              </div>
            )}

            {/* Linke Spalte: Kunde + Rechnungsinformationen */}
            <div className="w-full max-w-full space-y-6 lg:order-1">
              {/* Kunde auswählen */}
              <Card className="w-full max-w-full">
                <CardHeader>
                  <CardTitle>{t("invoices.form.customerSection")}</CardTitle>
                  <CardDescription>
                    {t("invoices.form.customerSectionDesc")}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <FormField
                    control={form.control}
                    name="customerId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t("invoices.form.customerLabel")} *
                        </FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                          disabled={isReadOnly || isEditMode}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue
                                placeholder={t(
                                  "invoices.form.customerPlaceholder",
                                )}
                              />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {customers?.map((customer) => {
                              const name =
                                customer.customerType === "business"
                                  ? customer.companyName
                                  : `${customer.firstName} ${customer.lastName}`;
                              return (
                                <SelectItem
                                  key={customer.id}
                                  value={customer.id}
                                >
                                  {name} ({customer.customerNumber})
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* Rechnungsinformationen */}
              <Card className="w-full max-w-full">
                <CardHeader>
                  <CardTitle>{t("invoices.form.invoiceInfoSection")}</CardTitle>
                  <CardDescription>
                    {t("invoices.form.invoiceInfoSectionDesc")}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Rechnungstyp */}
                  <FormField
                    control={form.control}
                    name="invoiceType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t("invoices.form.invoiceTypeLabel")}
                        </FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                          disabled={isEditMode}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {INVOICE_TYPE_OPTIONS.map((option) => (
                              <SelectItem
                                key={option.value}
                                value={option.value}
                              >
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {field.value === "free" && (
                          <FormDescription className="text-amber-600">
                            {t("invoices.form.invoiceTypeFreeDesc")}
                          </FormDescription>
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="invoiceNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {t("invoices.form.invoiceNumberLabel")} *
                          </FormLabel>
                          <FormControl>
                            {isEditMode || !isStandard ? (
                              <Input placeholder="RE-2024-001" {...field} />
                            ) : (
                              <div>
                                {/* Hidden field to submit the invoice number */}
                                <input type="hidden" {...field} />
                                <Input
                                  placeholder="RE-2024-001"
                                  value={
                                    isLoadingNumber
                                      ? "Wird generiert..."
                                      : numberPreview?.number ||
                                        "Wird generiert..."
                                  }
                                  readOnly
                                  className="bg-muted cursor-default"
                                />
                              </div>
                            )}
                          </FormControl>
                          {!isEditMode && isStandard && (
                            <FormDescription>
                              Diese Nummer wird beim Speichern vergeben
                            </FormDescription>
                          )}
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="invoiceDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {t("invoices.form.invoiceDateLabel")} *
                          </FormLabel>
                          <FormControl>
                            <Input type="date" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="paymentTerms"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {t("invoices.form.paymentTermsLabel")} *
                          </FormLabel>
                          <Select
                            onValueChange={(value) =>
                              field.onChange(Number(value))
                            }
                            defaultValue={field.value.toString()}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue
                                  placeholder={t(
                                    "invoices.form.paymentTermsLabel",
                                  )}
                                />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {PAYMENT_TERMS_OPTIONS.map((option) => (
                                <SelectItem
                                  key={option.value}
                                  value={option.value.toString()}
                                >
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormDescription>
                            {t("invoices.form.paymentTermsDesc")}
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="purchaseOrderNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {t("invoices.form.purchaseOrderLabel")}
                          </FormLabel>
                          <FormControl>
                            <Input placeholder="PO-123456" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="leitwegId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t("invoices.form.leitwegIdLabel")}
                        </FormLabel>
                        <FormControl>
                          <Input placeholder="12345-67890-12-1" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="message"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("invoices.form.messageLabel")}</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder={t("invoices.form.messageDesc")}
                            className="resize-none"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* MwSt. Konfiguration */}
                  <div className="mt-4 border-t pt-4">
                    <h4 className="mb-3 font-medium">
                      {t("invoices.form.vatRate")}
                    </h4>

                    <div className="mb-4 grid grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="vatRate"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {t("invoices.form.vatRate")} *
                            </FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                max="100"
                                placeholder="z.B. 19"
                                disabled={form.watch("isSmallBusiness")}
                                {...field}
                              />
                            </FormControl>
                            <FormDescription>
                              Standard: 0%, 7% oder 19%
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="priceType"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Preisart *</FormLabel>
                            <Select
                              value={field.value}
                              onValueChange={field.onChange}
                              disabled={form.watch("isSmallBusiness")}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Preisart wählen" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="net">
                                  Netto (zzgl. MwSt.)
                                </SelectItem>
                                <SelectItem value="gross">
                                  Brutto (inkl. MwSt.)
                                </SelectItem>
                              </SelectContent>
                            </Select>
                            <FormDescription>
                              Netto: Preise ohne MwSt
                              <br />
                              Brutto: Preise inkl. MwSt.
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="shippingRateId"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Versandkosten (optional)</FormLabel>
                            <Select
                              value={field.value || undefined}
                              onValueChange={field.onChange}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Keine Versandkosten" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {shippingRates?.map((rate) => (
                                  <SelectItem key={rate.id} value={rate.id}>
                                    {rate.name} (
                                    {parseFloat(rate.price)
                                      .toFixed(2)
                                      .replace(".", ",")}{" "}
                                    €)
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormDescription>
                              Wählen Sie einen Versandtarif aus
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="isSmallBusiness"
                      render={({ field }) => (
                        <FormItem className="mb-4 flex flex-row items-start space-y-0 space-x-3">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={(checked) => {
                                field.onChange(checked);
                                if (checked) {
                                  form.setValue("vatRate", "0");
                                  form.setValue("priceType", "net");
                                } else {
                                  form.setValue(
                                    "vatRate",
                                    companyDetails?.defaultVatRate.toString() ||
                                      "19",
                                  );
                                }
                              }}
                            />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel className="cursor-pointer font-normal">
                              {t("invoices.form.smallBusiness")}
                            </FormLabel>
                            <p className="text-muted-foreground text-sm">
                              {t("invoices.form.smallBusinessDesc")}
                            </p>
                          </div>
                        </FormItem>
                      )}
                    />

                    {form.watch("isSmallBusiness") && (
                      <FormField
                        control={form.control}
                        name="smallBusinessNote"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {t("invoices.form.smallBusinessNote")}
                            </FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder="Gemäß § 19 UStG wird keine Umsatzsteuer berechnet."
                                className="resize-none"
                                rows={2}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Ende der linken Spalte */}
            </div>
          </div>

          {/* Volle Breite Komponenten */}
          <Card className="w-full max-w-full overflow-hidden">
            <CardHeader>
              <CardTitle>{t("invoices.form.itemsSection")}</CardTitle>
              <CardDescription>
                {t("invoices.form.itemsSectionDesc")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Gemeinsame Überschriften-Zeile - nur auf Desktop */}
              <div
                className="text-muted-foreground hidden gap-4 border-b pb-2 text-sm font-medium lg:grid"
                style={{
                  gridTemplateColumns:
                    "50px 2fr 140px 160px 80px 80px 110px 140px 120px",
                }}
              >
                <div>#</div>
                <div>{t("invoices.form.itemDescription")} *</div>
                <div>{t("invoices.form.itemProductType")} *</div>
                <div>{t("invoices.form.itemDate")} *</div>
                <div>{t("invoices.form.itemUnit")} *</div>
                <div>{t("invoices.form.itemQuantity")} *</div>
                <div>{t("invoices.form.itemUnitPrice")} (€) *</div>
                <div>{t("invoices.form.itemTotal")}</div>
                <div></div>
              </div>

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={fields.map((field) => field.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {fields.map((field, index) => (
                    <SortableInvoiceItem
                      key={field.id}
                      id={field.id}
                      index={index}
                      form={form}
                      calculateItemTotal={calculateItemTotal}
                      formatCurrency={formatCurrency}
                      onRemove={() => remove(index)}
                      onDuplicate={() => {
                        const currentItem = form.getValues(`items.${index}`);
                        insert(index + 1, {
                          description: currentItem.description,
                          productType: currentItem.productType,
                          itemDate: currentItem.itemDate,
                          quantity: currentItem.quantity,
                          unit: currentItem.unit,
                          unitPrice: currentItem.unitPrice,
                        });
                      }}
                      canRemove={fields.length > 1}
                    />
                  ))}
                </SortableContext>
              </DndContext>

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  append({
                    description: "",
                    productType: "goods" as ProductType,
                    itemDate: new Date().toISOString().split("T")[0] || "",
                    quantity: 1,
                    unit: "Stk.",
                    unitPrice: 0,
                  })
                }
              >
                <Plus className="mr-2 h-4 w-4" />
                {t("invoices.form.addItem")}
              </Button>
            </CardContent>
          </Card>

          <Card className="w-full max-w-full">
            <CardHeader>
              <CardTitle>{t("invoices.form.taxSection")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {form.watch("priceType") === "net" && (
                  <>
                    <div className="flex justify-between">
                      <span>Zwischensumme (Netto):</span>
                      <span>
                        {formatCurrency(
                          form
                            .watch("items")
                            .reduce(
                              (
                                sum: number,
                                item: InvoiceFormValues["items"][0],
                              ) => {
                                return (
                                  sum +
                                  calculateItemTotal(
                                    item.quantity || 0,
                                    item.unitPrice || 0,
                                  )
                                );
                              },
                              0,
                            ),
                        )}
                      </span>
                    </div>
                    {form.watch("shippingRateId") &&
                      shippingRates?.find(
                        (r) => r.id === form.watch("shippingRateId"),
                      ) && (
                        <div className="flex justify-between">
                          <span>Versandkosten:</span>
                          <span>
                            {formatCurrency(
                              parseFloat(
                                shippingRates.find(
                                  (r) => r.id === form.watch("shippingRateId"),
                                )?.price || "0",
                              ),
                            )}
                          </span>
                        </div>
                      )}
                    <div className="flex justify-between font-semibold">
                      <span>Summe (Netto):</span>
                      <span>{formatCurrency(calculateSubtotal())}</span>
                    </div>
                    {!form.watch("isSmallBusiness") && (
                      <div className="flex justify-between">
                        <span>zzgl. MwSt. ({form.watch("vatRate")}%):</span>
                        <span>
                          {formatCurrency(calculateTax(calculateSubtotal()))}
                        </span>
                      </div>
                    )}
                    {form.watch("isSmallBusiness") && (
                      <div className="text-muted-foreground flex justify-between text-sm">
                        <span>{t("invoices.form.smallBusinessDesc")}:</span>
                        <span>{formatCurrency(0)}</span>
                      </div>
                    )}
                  </>
                )}
                {form.watch("priceType") === "gross" && (
                  <>
                    <div className="flex justify-between">
                      <span>Zwischensumme (Brutto):</span>
                      <span>
                        {formatCurrency(
                          form
                            .watch("items")
                            .reduce(
                              (
                                sum: number,
                                item: InvoiceFormValues["items"][0],
                              ) => {
                                return (
                                  sum +
                                  calculateItemTotal(
                                    item.quantity || 0,
                                    item.unitPrice || 0,
                                  )
                                );
                              },
                              0,
                            ),
                        )}
                      </span>
                    </div>
                    {form.watch("shippingRateId") &&
                      shippingRates?.find(
                        (r) => r.id === form.watch("shippingRateId"),
                      ) && (
                        <div className="flex justify-between">
                          <span>Versandkosten:</span>
                          <span>
                            {formatCurrency(
                              parseFloat(
                                shippingRates.find(
                                  (r) => r.id === form.watch("shippingRateId"),
                                )?.price || "0",
                              ),
                            )}
                          </span>
                        </div>
                      )}
                    <div className="flex justify-between font-semibold">
                      <span>Gesamtsumme (Brutto):</span>
                      <span>{formatCurrency(calculateTotal())}</span>
                    </div>
                    {!form.watch("isSmallBusiness") && (
                      <>
                        <div className="text-muted-foreground flex justify-between text-sm">
                          <span>Summe netto:</span>
                          <span>{formatCurrency(calculateSubtotal())}</span>
                        </div>
                        <div className="text-muted-foreground flex justify-between text-sm">
                          <span>davon MwSt. ({form.watch("vatRate")}%):</span>
                          <span>
                            {formatCurrency(calculateTax(calculateSubtotal()))}
                          </span>
                        </div>
                      </>
                    )}
                  </>
                )}
                <div className="flex justify-between border-t pt-2 text-lg font-bold">
                  <span>Endbetrag:</span>
                  <span>{formatCurrency(calculateTotal())}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end space-x-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => window.history.back()}
            >
              {isReadOnly ? t("common.close") : t("common.cancel")}
            </Button>
            {!isReadOnly && (
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting
                  ? isEditMode
                    ? t("common.loading")
                    : t("common.loading")
                  : isEditMode
                    ? t("invoices.form.updateInvoice")
                    : t("invoices.form.createInvoice")}
              </Button>
            )}
          </div>
        </form>
      </Form>
    </div>
  );
}
